import React, { useState } from "react";
import { Calendar, Clock, MapPin, GraduationCap, ClipboardList } from "lucide-react";

const STUDENT_IMG =
    "https://images.pexels.com/photos/7616706/pexels-photo-7616706.jpeg?auto=compress&cs=tinysrgb&w=1200";

const PROGRAM_CONTENT = {
    bsc: {
        key: "bsc",
        label: "B.Sc.",
        tagline: "A Foundation In Data Science, Analytics, And Applied Technologies",
        overview: [
            "An Undergraduate Program Focused On Data Science, Analytics, Machine Learning, And Practical Computing Applications.",
            "The Curriculum Combines Academic Learning With Hands-On Exposure Through Projects, Internships, Analytical Tools, And Industry-Oriented Practices.",
        ],
        details: [
            { icon: Clock, label: "Duration", value: "3 Years" },
            { icon: ClipboardList, label: "Eligibility", value: "As Per Applicable Admission Criteria" },
            { icon: GraduationCap, label: "Mode", value: "Full-Time (Offline)" },
            { icon: MapPin, label: "Location", value: "Worli, South Mumbai" },
        ],
        sections: [
            {
                title: "Learning & Program Structure",
                points: [
                    "Industry-Integrated Curriculum Aligned With Current Technology Trends",
                    "Practical Exposure Through Projects, Datasets, And Analytical Applications",
                    "Coverage Of Tools And Technologies Including Python, R, SQL, TensorFlow, Power BI, And Tableau",
                    "Introduction To Machine Learning Concepts And Analytical Methodologies",
                    "Capstone Projects, Datathons, And Applied Learning Initiatives",
                ],
            },
            {
                title: "Academic & Industry Exposure",
                points: [
                    "Mandatory Internships As Part Of The Curriculum",
                    "Research Paper Presentation And Publication Opportunities",
                    "Certificate Courses On Emerging Technologies",
                    "Field Visits And Academic Engagement Activities",
                    "Guest Lectures And Sessions By Industry Professionals",
                ],
            },
            {
                title: "Career Support & Student Development",
                points: [
                    "Placement Guidance For Data Science And Analytics Roles",
                    "Resume Building, LinkedIn, And GitHub Profile Support",
                    "Career Expos And Industry Interaction Opportunities",
                    "Soft Skill Development And Holistic Learning Initiatives",
                    "Participation In Academic Fests, Social Work, And Community Activities",
                ],
            },
        ],
    },
    msc: {
        key: "msc",
        label: "M.Sc.",
        tagline: "Advanced Study In Data Science, Analytics, And Machine Learning",
        overview: [
            "A Postgraduate Program Focused On Advanced Analytical Methods, Machine Learning, And Data-Driven Problem Solving.",
            "The Program Combines Theoretical Understanding With Research-Oriented And Project-Based Learning Across Real-World Applications.",
        ],
        details: [
            { icon: Clock, label: "Duration", value: "2 Years" },
            { icon: ClipboardList, label: "Eligibility", value: "As Per Applicable Admission Criteria" },
            { icon: GraduationCap, label: "Mode", value: "Full-Time (Offline)" },
            { icon: MapPin, label: "Location", value: "Worli, South Mumbai" },
        ],
        sections: [
            {
                title: "Learning & Program Structure",
                points: [
                    "Advanced Coverage Of Machine Learning And Analytical Techniques",
                    "Supervised And Unsupervised Learning Methodologies",
                    "Introduction To Ensemble Methods And Deep Learning Concepts",
                    "Applied Learning Through Projects Using Real-World Datasets",
                    "Exposure To Industry Tools Including Python, TensorFlow, Power BI, Tableau, And Related Technologies",
                ],
            },
            {
                title: "Academic & Industry Exposure",
                points: [
                    "Research-Oriented Academic Approach",
                    "Opportunities For Research Paper Presentation And Publication",
                    "Internships, Boot Camps, And Industry Interaction Activities",
                    "Practical Exposure Through Datathons And Applied Projects",
                    "Sessions Conducted By Faculty And Visiting Industry Experts",
                ],
            },
            {
                title: "Career Support & Student Development",
                points: [
                    "Dedicated Placement And Career Guidance Support",
                    "Assistance With Professional Profile Development",
                    "Exposure To Emerging Technologies Through Certification Programs",
                    "Soft Skill Development And Interdisciplinary Activities",
                    "Participation In Academic, Social, And Community Engagement Initiatives",
                ],
            },
        ],
    },
};

function Bullet() {
    return (
        <span
            className="mt-2.5 inline-block h-1.5 w-1.5 rounded-full shrink-0"
            style={{ background: "#ffc004" }}
        />
    );
}

export default function ProgramSelection() {
    const [active, setActive] = useState("bsc");
    const content = PROGRAM_CONTENT[active];

    return (
        <section
            id="programs"
            data-testid="program-selection-section"
            className="relative py-20 sm:py-24 md:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
                <p
                    className="text-[11px] tracking-[0.22em] uppercase font-semibold mb-4"
                    style={{ color: "#2e326b" }}
                >
                    Program Selection
                </p>

                <div className="grid lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16 items-start">
                    {/* LEFT - Image */}
                    <div className="lg:col-span-5 order-2 lg:order-1">
                        <div className="relative max-w-full">
                            <div
                                className="absolute -top-4 -left-4 sm:-top-5 sm:-left-5 h-24 w-24 sm:h-32 sm:w-32 rounded-2xl -z-0"
                                style={{ background: "#ffc004", opacity: 0.95 }}
                            />
                            <div
                                className="absolute -bottom-4 -right-4 sm:-bottom-5 sm:-right-5 h-28 w-28 sm:h-40 sm:w-40 rounded-2xl -z-0"
                                style={{ background: "rgba(46,50,107,0.12)" }}
                            />
                            <img
                                src={STUDENT_IMG}
                                alt="Indian Student Pursuing Data Science"
                                className="relative z-10 w-full h-[380px] sm:h-[480px] md:h-[560px] lg:h-[600px] object-cover rounded-2xl shadow-soft"
                                data-testid="program-student-image"
                            />
                            <p
                                className="relative z-10 mt-5 sm:mt-6 max-w-sm text-sm sm:text-base font-medium leading-relaxed"
                                style={{ color: "#2e326b" }}
                            >
                                Where Confidence Meets Capability — Built For The Next Generation Of Data Leaders.
                            </p>
                        </div>
                    </div>

                    {/* RIGHT - Heading + Sticky Tabs + Content */}
                    <div className="lg:col-span-7 order-1 lg:order-2">
                        {/* Heading sits above the tabs and scrolls away normally */}
                        <h2
                            className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight tracking-tight mb-8 sm:mb-10"
                            style={{ color: "#0f172a" }}
                        >
                            Choose The Program
                            <br />
                            <span style={{ color: "#2e326b" }}>That Defines Your Path.</span>
                        </h2>

                        {/* Sticky Tabs - stick just below the fixed header within this section */}
                        <div
                            className="sticky top-24 sm:top-28 md:top-32 z-30 -mx-4 sm:-mx-6 md:-mx-10 px-4 sm:px-6 md:px-10 py-3 sm:py-4 bg-white/95 backdrop-blur-md border-b border-slate-200/70"
                            data-testid="program-tabs-bar"
                        >
                            <div className="inline-flex p-1.5 rounded-full border border-slate-200 bg-slate-50/80">
                                {Object.values(PROGRAM_CONTENT).map((p) => {
                                    const isActive = active === p.key;
                                    return (
                                        <button
                                            key={p.key}
                                            type="button"
                                            onClick={() => setActive(p.key)}
                                            data-testid={`program-tab-${p.key}`}
                                            className={`tab-pill px-5 sm:px-7 md:px-8 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-bold tracking-wide ${
                                                isActive
                                                    ? "text-white shadow-floating"
                                                    : "text-slate-600 hover:text-slate-900"
                                            }`}
                                            style={
                                                isActive
                                                    ? { background: "#2e326b" }
                                                    : { background: "transparent" }
                                            }
                                        >
                                            {p.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div
                            key={content.key}
                            data-testid={`program-content-${content.key}`}
                            className="mt-8 sm:mt-10 animate-fade-up"
                        >
                            <p
                                className="text-lg sm:text-xl md:text-2xl font-medium leading-snug"
                                style={{ color: "#2e326b" }}
                                data-testid="program-tagline"
                            >
                                {content.tagline}
                            </p>

                            <div className="mt-8 sm:mt-10">
                                <h3
                                    className="text-[11px] tracking-[0.22em] uppercase font-semibold mb-4"
                                    style={{ color: "#0f172a" }}
                                >
                                    Course Overview
                                </h3>
                                <div className="space-y-4">
                                    {content.overview.map((line, i) => (
                                        <p
                                            key={i}
                                            className="text-base md:text-lg text-slate-700 leading-relaxed max-w-2xl"
                                        >
                                            {line}
                                        </p>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-10 sm:mt-12">
                                <h3
                                    className="text-[11px] tracking-[0.22em] uppercase font-semibold mb-6"
                                    style={{ color: "#0f172a" }}
                                >
                                    Program Details
                                </h3>
                                <div className="grid sm:grid-cols-2 gap-x-6 sm:gap-x-8 gap-y-5">
                                    {content.details.map((d, i) => (
                                        <div key={i} className="flex items-start gap-3.5">
                                            <div
                                                className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                                                style={{ background: "rgba(255,192,4,0.18)" }}
                                            >
                                                <d.icon
                                                    className="h-4 w-4"
                                                    strokeWidth={1.8}
                                                    style={{ color: "#2e326b" }}
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500">
                                                    {d.label}
                                                </p>
                                                <p
                                                    className="text-base font-semibold mt-0.5 break-words"
                                                    style={{ color: "#0f172a" }}
                                                >
                                                    {d.value}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {content.sections.map((sec, idx) => (
                                <div key={idx} className="mt-10 sm:mt-12">
                                    <h3
                                        className="text-[11px] tracking-[0.22em] uppercase font-semibold mb-5"
                                        style={{ color: "#0f172a" }}
                                    >
                                        {sec.title}
                                    </h3>
                                    <ul className="space-y-3.5">
                                        {sec.points.map((p, i) => (
                                            <li key={i} className="flex items-start gap-3.5">
                                                <Bullet />
                                                <span className="text-base md:text-[17px] text-slate-700 leading-relaxed">
                                                    {p}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
